import type { Dictionary } from '@/i18n/dictionary'
import { DEFAULT_LOCALE } from '@/i18n/locales'

/**
 * The page registry — one entry per page.
 *
 * Routing, the header nav, the sitemap and the prerender pass all read
 * `ROUTES`, so a page cannot be missing from the sitemap or the prerender —
 * which is exactly the failure that leaves a page invisible to search engines
 * and nobody notices for a month.
 *
 * `ROUTES` is two things joined: `CURATED` below, and the routes page modules
 * declare for themselves. The second half exists because five concurrent runs
 * appending to one array is a push conflict rather than a merge; see the note
 * above `DECLARED`.
 */
import { routesFrom } from '@/content/pageRegistry'
import { published } from '@/content/drafts'

export interface RouteDef {
  /**
   * Key into `Dictionary['pages']`, and the sitemap's stable identifier.
   *
   * This used to be `keyof Dictionary['pages']`, which made "a route without
   * copy" a compile error. `content/en.ts` now discovers its pages by glob, so
   * that key is `string` and the compiler can no longer name the missing one.
   * The check did not go away — it moved to `scripts/check-routes.mjs`, run as
   * the first step of `pnpm build`, which fails the build and names every route
   * whose `id` has no page. A blank page that prerenders happily is exactly the
   * failure this guards, so it has to fail *somewhere*; build time is the
   * cheapest place left.
   */
  id: keyof Dictionary['pages']
  /** Path *after* the locale segment. `''` is that locale's index. */
  path: string
  /**
   * `'noindex'` keeps a page out of search results AND out of the sitemap.
   *
   * For pages that are the OUTCOME of a private action rather than something
   * anybody would search for — the newsletter confirm/unsubscribe landings are
   * the first three. A "you are subscribed" page in Google's index is a page
   * people arrive at without having subscribed, and it dilutes the site's own
   * results with copy that answers no question.
   *
   * Site-wide indexability is separate and still wins: a preview build is
   * `noindex` everywhere regardless of this. This only ever subtracts.
   */
  robots?: 'noindex'
  /**
   * Sitemap priority, 0–1. Relative weight within our own site; it is a hint,
   * and search engines treat it as one.
   */
  priority: number
  /**
   * Not published. Built and navigable on preview, absent from production —
   * no page, no sitemap entry, no nav item, 404 on www.
   *
   * See `content/drafts.ts` for why this exists and why it is not the same
   * question as "should the site go live". The filter is applied to `ROUTES`
   * below, once, so every consumer gets it without knowing.
   */
  draft?: boolean
  /**
   * The locales this page is written in, when that is not all of them.
   *
   * Absent means every live locale, which is what every written page was until
   * 2026-10-08 — a locale with no copy of its own falls back to English under
   * its own prefix (the careers pages do this today). Fatih, on `/ai-impact`:
   * *"/tr/ai-impact → /en/ai-impact 301: evet."* An English page under `/tr/` is
   * a self-canonical, indexable duplicate of the English one, so a page that is
   * deliberately English-only says so here and the other locales simply do not
   * get a document: no prerendered file, no sitemap entry, no `hreflang` naming
   * it, and no language switcher offering it.
   *
   * What an old `/tr/...` address does instead is a REDIRECT, and that is a row
   * in `src/redirects.moved.ts`, not something this field does. Kept apart on
   * purpose: a 301 may not land on a draft (`verify-redirects.mjs`), so the row
   * waits for the change that takes the page out of draft, while this field is
   * true from the day it is written.
   */
  locales?: string[]
}

/** Whether `route` has a document in `locale`. See `RouteDef.locales`. */
export function routeIn(route: RouteDef, locale: string): boolean {
  return !route.locales || route.locales.includes(locale)
}

/**
 * The written pages, in the order Halil's copy document specifies them.
 *
 * Six entries for five specified pages: `/solutions` is not in the document, and
 * exists because `/solutions/` redirects there (and `/solutions/maturity-frameworks/`
 * did until 2026-10-02, when it moved to the DAIMI page). It reuses the home
 * page's three cards rather than restating them.
 *
 * `Platform` is deliberately absent — the platforms consolidate under
 * `impact.digitopia.ai`, so the header links out rather than in. The rest of the
 * navigation (Customers, Partners, Resources) points at imported legacy pages and
 * lives in `nav.items` in the dictionary, because deriving the nav from this
 * registry would silently drop everything that was imported rather than written.
 *
 * Deliberately deferred, so nobody rebuilds it from the transcript: an image-free
 * plain-Markdown "Agent View" of the site for GEO. Discussed, liked, not now.
 */
const CURATED: RouteDef[] = [
  { id: 'home', path: '', priority: 1 },
  { id: 'solutions', path: 'solutions', priority: 0.9 },
  { id: 'strategyConsulting', path: 'solutions/strategy-consulting', priority: 0.9 },
  { id: 'transformationManagement', path: 'solutions/transformation-management', priority: 0.9 },
  { id: 'enterpriseAiSolutions', path: 'solutions/enterprise-ai-solutions', priority: 0.9 },
  { id: 'about', path: 'about', priority: 0.8 },
  // The Partner Program. It keeps the slug the imported WordPress page held —
  // the header, the footer and `/partner-program` in the redirect map all point
  // at it, and an inbound link to digitopia.co's own partner page lands here.
  // The imported page is still on disk as the record of what it used to say and
  // is skipped at build time; see `SUPERSEDED_BY_WRITTEN` in `src/legacy.ts`,
  // which is what stops it overwriting this one's HTML.
  { id: 'partnerProgram', path: 'partner-program', priority: 0.8 },
  // The assessment tools. `tools` is the hub Kagan asked for — three peers,
  // each with its own button, rather than one tool funnelling into another.
  { id: 'tools', path: 'tools', priority: 0.8 },
  { id: 'toolsGutsCheck', path: 'tools/agentic-guts-check', priority: 0.7 },
  { id: 'toolsDiagnostic', path: 'tools/transformation-readiness', priority: 0.7 },
  { id: 'toolsAri', path: 'tools/agentic-readiness-index', priority: 0.8 },
  // The Transform Better e-book. It keeps digitopia.co's own path so the
  // inbound links and the QR codes printed on the book itself still land, and
  // it is a written page rather than an imported one because the page it
  // replaces was never scraped — only the e-books index above it was.
  { id: 'ebookTransformBetter', path: 'impact-labs/ebooks/transform-better-e-book', priority: 0.7 },
  // The Agentic AI e-book, the second one designed rather than templated. Same
  // reasoning as the line above, and the same path digitopia.co uses — it is one
  // of the two e-book slugs the redirect table actually confirms.
  { id: 'ebookAgenticAi', path: 'impact-labs/ebooks/agentic-ai', priority: 0.7 },
  // The Sustainability Innovation e-book, the third designed one. Its path is
  // digitopia.co's — the reference screenshots were captured from
  // `/impact-labs/ebooks/sustainability-innovation/`, which is why the
  // catalogue's derived `leading-the-charge-in-…` slug was corrected rather
  // than kept.
  {
    id: 'ebookSustainabilityInnovation',
    path: 'impact-labs/ebooks/sustainability-innovation',
    priority: 0.7,
  },
  // The measurement e-book, the fourth designed one. Its path is digitopia.co's
  // — the reference screenshots were captured from
  // `/impact-labs/ebooks/importance-of-measurement-for-transformation-success/`,
  // which is why the catalogue's derived `whats-measured-gets-done` slug was
  // corrected rather than kept. Second time the title rule has been wrong.
  {
    id: 'ebookMeasurement',
    path: 'impact-labs/ebooks/importance-of-measurement-for-transformation-success',
    priority: 0.7,
  },
  // The fashion-retail shopping-season e-book, the fifth designed one. Its path
  // is digitopia.co's — the reference screenshots were captured from
  // `/impact-labs/ebooks/ai-in-fashion-retail/`, which is why the catalogue's
  // derived `how-fashion-leaders-are-preparing-the-shopping-season` slug was
  // corrected rather than kept. Third time the title rule has been wrong.
  { id: 'ebookAiInFashionRetail', path: 'impact-labs/ebooks/ai-in-fashion-retail', priority: 0.7 },
  // The Generative AI leadership guide, the sixth designed one. Its path is
  // digitopia.co's — the reference screenshots were captured from
  // `/impact-labs/ebooks/leaders-guide-to-generative-ai/` and carry that address
  // in their filenames, which is why the catalogue's derived
  // `the-leaders-guide-to-generative-ai` slug was corrected rather than kept.
  // Fourth time the title rule has been wrong.
  {
    id: 'ebookGenerativeAi',
    path: 'impact-labs/ebooks/leaders-guide-to-generative-ai',
    priority: 0.7,
  },
]

/**
 * Routes a page module declares for itself.
 *
 * `CURATED` above is every route that existed on 2026-08-28. It stays a list
 * because most of its entries carry a paragraph explaining a slug decision, and
 * those paragraphs belong next to each other. Everything added since declares
 * its own route, in the same file as the page's copy:
 *
 *     // content/pages/ebook-something.ts
 *     export const route: RouteDef = {
 *       id: 'ebookSomething',
 *       path: 'impact-labs/ebooks/something',
 *       priority: 0.7,
 *     }
 *
 * `id` must match the export name of the `WrittenPage` in that same file, since
 * `App.tsx` and `lib/seo.ts` resolve a route to its copy with
 * `t.pages[route.id]`. `scripts/check-routes.mjs`, wired into `pnpm build`,
 * refuses a build where they disagree — see the note on `id` above for why that
 * check moved out of the compiler.
 *
 * WHY THIS IS NOT ONE LIST. Adding one e-book page used to mean editing this
 * array, plus an import and a key in each of `content/en.ts` and
 * `content/tr.ts`, plus two keys in `content/pages/links.ts` — five appends
 * into five small regions. Two concurrent AI-editor runs on unrelated pages
 * therefore wrote the same lines and the second could not rebase. On
 * 2026-08-28, seven agent runs were thrown away against three that landed, and
 * one request reached the three-try cap, before this changed.
 */
const DECLARED = routesFrom(
  import.meta.glob<Record<string, unknown>>('@/content/pages/*.ts', { eager: true }),
)

export const ROUTES: RouteDef[] = published([...CURATED, ...DECLARED])

/** Every route including the unpublished ones — the build's draft report reads this. */
export const ALL_ROUTES: RouteDef[] = [...CURATED, ...DECLARED]


/**
 * The URL for a route in a locale.
 *
 * **Every locale is prefixed, English included** — `/en`, not `/`. The
 * asymmetric alternative (English at the root, everyone else under a prefix)
 * gives one language a privileged URL shape, and then every link helper, every
 * canonical and every `hreflang` needs the special case. `/` redirects, which
 * is one rule in the Worker instead of a branch in a dozen places.
 */
export function localePath(locale: string, path: string): string {
  return path ? `/${locale}/${path}` : `/${locale}`
}

/** Every (locale, route) pair the build has to emit. */
export function allPages(locales: string[]): { locale: string; route: RouteDef; url: string }[] {
  return locales.flatMap((locale) =>
    ROUTES.filter((route) => routeIn(route, locale)).map((route) => ({
      locale,
      route,
      url: localePath(locale, route.path),
    })),
  )
}

export function routeForPath(path: string): RouteDef | undefined {
  return ROUTES.find((r) => r.path === path)
}

export const HOME_URL = localePath(DEFAULT_LOCALE, '')
